import React, { useState } from "react";
import { toast } from "react-toastify";
import Button from "../Button";
import { AiFigure } from "../../types/Ai";
import { Cell } from "../../types/bd/Research";
import ResearchFigure, { figureLabel } from "../bd/research/ResearchFigure";
import { ResearchTableView } from "../bd/research/ResearchAnswer";
import ResearchTableEditor from "../bd/research/ResearchTableEditor";
import { downloadFigurePng } from "../../utils/bd/researchExport";
import { askFigure } from "../../utils/ask/askFigure";

interface AskFiguresProps {
    figures?: AiFigure[];
    messageId: number;
    /** Save a table changed by hand (PUT …/figure); left out when the answer can't be edited. */
    onEdit?: (index: number, table: { columns: string[]; rows: Cell[][] }) => Promise<boolean>;
}

/**
 * The charts, diagrams, range checks and tables under an Ask answer, drawn by
 * CRX from the lookup's own numbers with BD › Research's parts (Download PNG,
 * Download as Excel, Edit).
 */
const AskFigures: React.FC<AskFiguresProps> = ({ figures, messageId, onEdit }) => {
    const [editing, setEditing] = useState<number | null>(null);
    if (!figures?.length) return null;
    const today = new Date().toISOString().slice(0, 10);
    return (
        <>
            {figures.map((raw, index) => {
                // the figure's place stays its index in the answer (the export finds its picture by it)
                const figure = askFigure(raw);
                if (!figure) return null;
                if (figure.type === "table") {
                    if (editing === index && onEdit) {
                        return <ResearchTableEditor key={index} table={figure} onSave={(table) => onEdit(index, table)} onCancel={() => setEditing(null)} />;
                    }
                    return (
                        <ResearchTableView key={index} table={figure} isEdited={figure.edited} actions={onEdit && (
                            <Button size="xs" variant="ghost" icon="pencil-square" onClick={() => setEditing(index)}
                                title="Fix a number, add a row you know, or drop rows; the table is marked Edited after you save">Edit</Button>
                        )} />
                    );
                }
                const svgId = `ask-figure-${messageId}-${index}`;
                const png = async () => {
                    if (!(await downloadFigurePng(svgId, figure.title || figureLabel(figure)))) toast.error("The picture couldn't be made in this browser.");
                };
                return <ResearchFigure key={index} spec={figure} svgId={svgId} today={today} onDownload={png} />;
            })}
        </>
    );
};

export default AskFigures;
